import { Anchor, Button, Card, Center, Stack, Text } from '@mantine/core'
import type { ReactNode } from 'react'

import type { Suspension } from '../../api/client'
import { useAuth } from '../../auth/AuthContext'
import { useSignOutAndClear } from '../../auth/useSignOutAndClear'
import { StatusBanner } from '../../components/StatusBanner'
import { formatDeleteDate } from './DeleteAccountAction'

/** The one page a suspended account may still reach (docs/plan/api.md#moderation: every other route 403s). */
const EXPORT_PATH = '/export/download'

/**
 * Blocks the signed-in app for a suspended account (services/api suspension.py, docs/plan/api.md#moderation):
 * every route 403s except export, so this replaces the whole app with the suspension notice rather than letting
 * each page fail on its own sync call. Sits outside ConsentGate and the router in main.tsx, so a suspended
 * account never reaches either. `/export/download` is let through by its pathname (checked here, not through the
 * router, since this sits above it) because the account can still export its data.
 *
 * ponytail: the pathname is only checked at mount, not on every client-side navigation, so a suspended account
 * that reaches /export/download could use the signed-in app's own nav to wander elsewhere in that tab. Closing
 * that needs a router-level guard with auth in its context; add one if this becomes a real problem.
 */
export function SuspendedGate(props: { children: ReactNode }) {
  const auth = useAuth()
  if (!auth.suspension || window.location.pathname === EXPORT_PATH) return <>{props.children}</>
  return <SuspendedScreen suspension={auth.suspension} />
}

function SuspendedScreen(props: { suspension: Suspension }) {
  const signOutAndClear = useSignOutAndClear()
  return (
    <Center mih="100vh" p="md">
      <Card padding="lg" maw={480} w="100%">
        <Stack gap="md">
          <Text fw={600} fz="lg">
            Account suspended
          </Text>
          <StatusBanner tone="error">
            {props.suspension.reasonText ?? 'Your account is suspended'} and scheduled for deletion on {formatDeleteDate(props.suspension.deleteAt)}.
          </StatusBanner>
          <Text size="sm">
            You can still download a copy of your data below. To appeal, email{' '}
            <Anchor href={`mailto:${props.suspension.appealEmail}`}>{props.suspension.appealEmail}</Anchor> — a
            person reviews every suspension.
          </Text>
          <Button component="a" href={EXPORT_PATH} variant="light">
            Download your data
          </Button>
          <Button variant="subtle" color="gray" onClick={() => void signOutAndClear()}>
            Sign out
          </Button>
        </Stack>
      </Card>
    </Center>
  )
}
